import { useEffect, useState } from "react";
import { Sparkle, ArrowClockwise, TrendUp, TrendDown, Scales, FilePdf, FileXls, PaperPlaneTilt, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import { toast } from "sonner";
import api, { downloadFile } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";

function renderInsights(text) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} className="h-2" />;
    if (/^#{1,6}\s/.test(t))
      return <h3 key={i} className="font-heading text-base text-primary mt-4 mb-1">{t.replace(/^#{1,6}\s/, "")}</h3>;
    if (/^\*\*.+\*\*:?$/.test(t))
      return <h3 key={i} className="font-heading text-base text-primary mt-4 mb-1">{t.replace(/\*\*/g, "").replace(/:$/, "")}</h3>;
    if (/^[-*•]\s/.test(t))
      return <li key={i} className="text-sm text-foreground/85 ml-5 list-disc leading-relaxed">{t.replace(/^[-*•]\s/, "").replace(/\*\*/g, "")}</li>;
    if (/^\d+\.\s/.test(t))
      return <li key={i} className="text-sm text-foreground/85 ml-5 list-decimal leading-relaxed">{t.replace(/^\d+\.\s/, "").replace(/\*\*/g, "")}</li>;
    return <p key={i} className="text-sm text-foreground/85 leading-relaxed">{t.replace(/\*\*/g, "")}</p>;
  });
}

export default function Reports() {
  const { display, currencies } = useCurrency();
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [insights, setInsights] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get(`/dashboard?display=${display}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/forecast?display=${display}`).then((r) => setForecast(r.data)).catch(() => {});
  }, [display]);

  const sendEmail = async () => {
    setEmailing(true);
    try {
      const { data: res } = await api.post("/reports/email");
      toast.success(`Rapor ${res.to} adresine gönderildi`);
    } catch (e) {
      toast.error("E-posta gönderilemedi");
    } finally {
      setEmailing(false);
    }
  };

  const doExport = async (format) => {
    setExporting(format);
    try {
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      await downloadFile(`/reports/export?format=${format}&display=${display}`, `beyt-kasa-rapor.${ext}`);
      toast.success(`Rapor ${ext.toUpperCase()} olarak indirildi`);
    } catch (e) {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting("");
    }
  };

  const generate = async () => {
    setLoading(true);
    setInsights("");
    try {
      const { data: res } = await api.post(`/ai/insights?display=${display}`);
      setInsights(res.insights);
    } catch (e) {
      toast.error("AI analizi oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const fm = (v) => formatMoney(v, display, currencies);

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Raporlar & AI Analizi</h1>
          <p className="text-sm text-muted-foreground mt-1">Finansal durumunuzun yapay zeka destekli değerlendirmesi</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={sendEmail} disabled={emailing} data-testid="email-report-btn"
            variant="outline" className="border-border hover:bg-secondary">
            <PaperPlaneTilt size={18} className="mr-1.5 text-primary" /> {emailing ? "..." : "E-posta"}
          </Button>
          <Button onClick={() => doExport("xlsx")} disabled={exporting === "xlsx"} data-testid="export-excel-btn"
            variant="outline" className="border-border hover:bg-secondary">
            <FileXls size={18} className="mr-1.5 text-[#10B981]" /> {exporting === "xlsx" ? "..." : "Excel"}
          </Button>
          <Button onClick={() => doExport("pdf")} disabled={exporting === "pdf"} data-testid="export-pdf-btn"
            variant="outline" className="border-border hover:bg-secondary">
            <FilePdf size={18} className="mr-1.5 text-[#EF4444]" /> {exporting === "pdf" ? "..." : "PDF"}
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 text-muted-foreground"><TrendUp size={18} className="text-[#10B981]" /><span className="text-xs uppercase tracking-wide">Gelir</span></div>
            <div className="mt-3 font-heading text-xl font-mono-num text-[#10B981]">{fm(data.total_income)}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 text-muted-foreground"><TrendDown size={18} className="text-[#EF4444]" /><span className="text-xs uppercase tracking-wide">Gider</span></div>
            <div className="mt-3 font-heading text-xl font-mono-num text-[#EF4444]">{fm(data.total_expense)}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 text-muted-foreground"><Scales size={18} className="text-primary" /><span className="text-xs uppercase tracking-wide">Net</span></div>
            <div className={`mt-3 font-heading text-xl font-mono-num ${data.net_cashflow >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{fm(data.net_cashflow)}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="font-heading text-lg mb-6">Gelir / Gider Karşılaştırması</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }}
                  formatter={(v, n) => [fm(v), n === "income" ? "Gelir" : "Gider"]} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="income" fill="#10B981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expense" fill="#EF4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {forecast && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-1">
            <ChartLineUp size={20} className="text-primary" />
            <h2 className="font-heading text-lg">Nakit Akışı Tahmini (Gelecek 3 Ay)</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            Geçmiş ortalamalar, bekleyen ödeme/tahsilatlar ve sabit ödemelere göre öngörü. Başlangıç bakiyesi: {fm(forecast.starting_balance)}
          </p>
          <div className="h-72" data-testid="forecast-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={forecast.forecast}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }}
                  formatter={(v, n) => [fm(v), n === "income" ? "Gelir" : n === "expense" ? "Gider" : "Tahmini Bakiye"]} cursor={{ fill: "#ffffff08" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => (v === "income" ? "Gelir" : v === "expense" ? "Gider" : "Tahmini Bakiye")} />
                <Bar dataKey="income" fill="#10B981" radius={[3, 3, 0, 0]} barSize={24} />
                <Bar dataKey="expense" fill="#EF4444" radius={[3, 3, 0, 0]} barSize={24} />
                <Line type="monotone" dataKey="balance" stroke="#D4AF37" strokeWidth={2.5} dot={{ r: 4, fill: "#D4AF37" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {forecast.forecast.map((m) => (
              <div key={m.month} className="border border-border rounded-md p-3">
                <div className="text-xs text-muted-foreground">{m.month}</div>
                <div className={`font-mono-num font-semibold mt-1 ${m.net >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {m.net >= 0 ? "+" : ""}{fm(m.net)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">Tahmini bakiye: {fm(m.balance)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      <div className="relative rounded-lg border border-primary/30 bg-card p-6 md:p-8 ai-glow overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1628331507643-ddf8016ca45d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')" }}
        />
        <div className="relative">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center">
                <Sparkle size={22} weight="fill" className="text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-lg">AI Finansal Analiz</h2>
                <p className="text-xs text-muted-foreground">Claude tarafından desteklenmektedir</p>
              </div>
            </div>
            <Button onClick={generate} disabled={loading} data-testid="generate-insights-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
              {loading ? <ArrowClockwise size={18} className="mr-1.5 animate-spin" /> : <Sparkle size={18} weight="fill" className="mr-1.5" />}
              {loading ? "Analiz ediliyor..." : insights ? "Yeniden Analiz Et" : "Analiz Oluştur"}
            </Button>
          </div>

          {!insights && !loading && (
            <p className="text-sm text-muted-foreground" data-testid="insights-empty">
              Finansal verilerinizi yapay zeka ile analiz etmek için "Analiz Oluştur" butonuna tıklayın. Nakit akışı sağlığı, tasarruf önerileri ve dikkat edilmesi gereken noktalar hakkında öneriler alacaksınız.
            </p>
          )}
          {loading && (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => <div key={i} className="h-3 rounded bg-secondary animate-pulse" style={{ width: `${70 + (i % 3) * 10}%` }} />)}
            </div>
          )}
          {insights && (
            <div className="space-y-1 max-w-3xl" data-testid="insights-content">{renderInsights(insights)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
